import { useEffect, useState } from "react";
import api from "../lib/api";
import Navbar from "../components/Navbar";
import { useLang } from "../contexts/LanguageContext";
import { Card } from "../components/ui/card";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Badge } from "../components/ui/badge";
import { ScrollText, MapPin, AlertTriangle } from "lucide-react";

const CATS = ["all", "lane", "edge", "stop", "yield", "pedestrian"];

export default function RoadMarkings() {
  const { t, lang } = useLang();
  const [items, setItems] = useState([]);
  const [cat, setCat] = useState("all");
  const [openId, setOpenId] = useState(null);
  const [openImg, setOpenImg] = useState(null);
  const [openName, setOpenName] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  useEffect(() => {
    const params = { lang };
    if (cat !== "all") params.category = cat;
    api.get("/road-markings", { params }).then(r => setItems(r.data));
  }, [cat, lang]);

  const open = (m) => {
    setOpenId(m.marking_id);
    setOpenImg(m.image);
    setOpenName(m.name);
    setDetail(null);
    setDetailLoading(true);
    api.get(`/road-markings/detail/${m.marking_id}?lang=${lang}`)
      .then(r => setDetail(r.data))
      .catch(() => setDetail(null))
      .finally(() => setDetailLoading(false));
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar/>
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <h1 data-testid="markings-title" className="text-3xl sm:text-4xl font-bold tracking-tight mb-2">{t("markingsTitle")}</h1>
        <p className="text-gray-600 mb-8">{t("markingsSubtitle")}</p>

        <Tabs value={cat} onValueChange={setCat} className="mb-8">
          <TabsList className="flex flex-wrap h-auto bg-gray-100 p-1">
            {CATS.map(c => (
              <TabsTrigger key={c} value={c} data-testid={`markings-cat-${c}`} className="data-[state=active]:bg-white data-[state=active]:text-fjord">
                {t(c) !== c ? t(c) : c}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 stagger">
          {items.map(m => (
            <Card
              key={m.marking_id}
              data-testid={`marking-${m.marking_id}`}
              onClick={() => open(m)}
              className="p-5 bg-white border border-gray-200 hover:border-fjord/40 hover:shadow-md cursor-pointer transition-all"
            >
              <div className="aspect-[16/9] bg-gray-50 rounded-md grid place-items-center mb-3 p-2 border border-gray-100 overflow-hidden">
                <img src={m.image} alt={m.name} className="max-h-full max-w-full object-contain"/>
              </div>
              <div className="text-xs uppercase tracking-wide text-fjord font-semibold mb-1">{m.category}</div>
              <h3 className="font-heading font-semibold mb-1">{m.name}</h3>
              <p className="text-sm text-gray-600 leading-snug line-clamp-2">{m.description}</p>
            </Card>
          ))}
        </div>
      </main>

      <Dialog open={!!openId} onOpenChange={(o) => !o && setOpenId(null)}>
        <DialogContent
          data-testid="marking-detail-modal"
          className="max-w-3xl max-h-[90vh] overflow-y-auto"
        >
          <DialogHeader>
            <DialogTitle className="text-start">{openName || `Marking ${openId}`}</DialogTitle>
          </DialogHeader>

          <div className="aspect-[16/9] bg-gray-50 rounded-md grid place-items-center p-2 border border-gray-100">
            {openImg && <img src={openImg} alt={openName} className="max-h-full max-w-full object-contain"/>}
          </div>

          <div className="space-y-3 mt-2">
            {detailLoading && (
              <p className="text-sm text-gray-400">Loading…</p>
            )}
            {!detailLoading && !detail && (
              <p className="text-sm text-gray-500" data-testid="marking-detail-pending">
                Detailed explanation is coming soon. The marking image and code are authoritative per Skiltforskriften §22.
              </p>
            )}
            {detail?.definition && (
              <p className="text-base text-gray-900 leading-relaxed">{detail.definition}</p>
            )}

            {detail?.when_you_see_it && (
              <div className="bg-fjord/5 border border-fjord/20 rounded-md p-3">
                <div className="text-[10px] uppercase tracking-wide text-fjord font-semibold mb-1">When you see it</div>
                <p className="text-sm text-gray-800">{detail.when_you_see_it}</p>
              </div>
            )}

            {detail?.what_to_do && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-md p-3">
                <div className="text-[10px] uppercase tracking-wide text-emerald-700 font-semibold mb-1">What to do</div>
                <p className="text-sm text-emerald-900">{detail.what_to_do}</p>
              </div>
            )}

            {detail?.real_situations?.length > 0 && (
              <div className="bg-white border border-gray-200 rounded-md p-3" data-testid="marking-real-situations">
                <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wide text-gray-700 font-semibold mb-2">
                  <MapPin className="w-3 h-3"/> Real situations
                </div>
                {detail.real_situations.map((s, i) => (
                  <div key={i} className="mb-2 last:mb-0">
                    <div className="text-sm font-semibold text-gray-900">{s.title}</div>
                    <p className="text-xs text-gray-700">{s.description}</p>
                  </div>
                ))}
              </div>
            )}

            {detail?.common_confusions?.length > 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
                <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wide text-amber-800 font-semibold mb-2">
                  <AlertTriangle className="w-3 h-3"/> Often confused with
                </div>
                {detail.common_confusions.map((c, i) => (
                  <div key={i} className="text-sm mb-1.5 last:mb-0">
                    <span className="font-semibold text-amber-900">{c.confused_with}:</span>{" "}
                    <span className="text-amber-800">{c.difference}</span>
                  </div>
                ))}
              </div>
            )}

            {detail?.reference && (
              <Badge variant="outline" className="text-[10px] bg-white">
                <ScrollText className="w-3 h-3 me-1"/> {detail.reference}
              </Badge>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
